import React, { useCallback, useEffect, useState } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';

// Define the MenuBar component inside this file to keep it self-contained
const MenuBar: React.FC<{ editor: Editor | null, isSourceMode: boolean, toggleSourceMode: () => void }> = ({ editor, isSourceMode, toggleSourceMode }) => {
  if (!editor) {
    return null;
  }

  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);

    if (url === null) return;

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const buttonClass = 'p-2 text-gray-700 hover:bg-gray-200 rounded-md transition-colors text-sm font-medium disabled:opacity-50';
  const activeClass = 'bg-indigo-100 text-indigo-700';

  return (
    <div className="flex flex-wrap items-center gap-1 p-2 border border-b-0 border-gray-300 rounded-t-md bg-gray-50">
      {!isSourceMode ? (
        <>
          <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={`${buttonClass} ${editor.isActive('bold') ? activeClass : ''}`} title="Bold"><strong>B</strong></button>
          <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={`${buttonClass} ${editor.isActive('italic') ? activeClass : ''}`} title="Italic"><em>I</em></button>
          <button type="button" onClick={() => editor.chain().focus().toggleStrike().run()} className={`${buttonClass} ${editor.isActive('strike') ? activeClass : ''}`} title="Strike"><s className="no-underline">S</s></button>
          <div className="w-[1px] h-6 bg-gray-300 mx-1"></div>
          <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className={`${buttonClass} ${editor.isActive('heading', { level: 1 }) ? activeClass : ''}`} title="Heading 1">H1</button>
          <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={`${buttonClass} ${editor.isActive('heading', { level: 2 }) ? activeClass : ''}`} title="Heading 2">H2</button>
          <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={`${buttonClass} ${editor.isActive('heading', { level: 3 }) ? activeClass : ''}`} title="Heading 3">H3</button>
          <button type="button" onClick={() => editor.chain().focus().setParagraph().run()} className={`${buttonClass} ${editor.isActive('paragraph') ? activeClass : ''}`} title="Paragraph">P</button>
          <div className="w-[1px] h-6 bg-gray-300 mx-1"></div>
          <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={`${buttonClass} ${editor.isActive('bulletList') ? activeClass : ''}`} title="Bullet List">UL</button>
          <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={`${buttonClass} ${editor.isActive('orderedList') ? activeClass : ''}`} title="Ordered List">OL</button>
          <button type="button" onClick={() => editor.chain().focus().toggleBlockquote().run()} className={`${buttonClass} ${editor.isActive('blockquote') ? activeClass : ''}`} title="Blockquote">"</button>
          <button type="button" onClick={() => editor.chain().focus().setHorizontalRule().run()} className={buttonClass} title="Horizontal Rule">―</button>
          <div className="w-[1px] h-6 bg-gray-300 mx-1"></div>
          <button type="button" onClick={setLink} className={`${buttonClass} ${editor.isActive('link') ? activeClass : ''}`} title="Add Link">Link</button>
          <div className="w-[1px] h-6 bg-gray-300 mx-1"></div>
          <button type="button" onClick={() => editor.chain().focus().setTextAlign('left').run()} className={`${buttonClass} ${editor.isActive({ textAlign: 'left' }) ? activeClass : ''}`} title="Align Left">Left</button>
          <button type="button" onClick={() => editor.chain().focus().setTextAlign('center').run()} className={`${buttonClass} ${editor.isActive({ textAlign: 'center' }) ? activeClass : ''}`} title="Align Center">Center</button>
          <button type="button" onClick={() => editor.chain().focus().setTextAlign('right').run()} className={`${buttonClass} ${editor.isActive({ textAlign: 'right' }) ? activeClass : ''}`} title="Align Right">Right</button>
          <div className="w-[1px] h-6 bg-gray-300 mx-1"></div>
          <button type="button" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} className={buttonClass} title="Undo">Undo</button>
          <button type="button" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} className={buttonClass} title="Redo">Redo</button>
        </>
      ) : (
         <span className="px-2 text-sm text-gray-500 font-medium flex items-center">
           Editing HTML Source Code
         </span>
      )}
      
      <div className="flex-grow"></div>
      <button 
        type="button" 
        onClick={toggleSourceMode} 
        className={`${buttonClass} bg-gray-200 hover:bg-gray-300 ml-2`} 
        title={isSourceMode ? "Switch to Visual Editor" : "Edit HTML Source"}
      >
        {isSourceMode ? "Visual Editor" : "HTML Source"}
      </button>
    </div>
  );
};

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
}

const extensions = [
  StarterKit,
  TextAlign.configure({
    types: ['heading', 'paragraph'],
  }),
  Link.configure({
    openOnClick: false,
    autolink: true,
    HTMLAttributes: {
      class: 'text-indigo-600 hover:text-indigo-800 underline',
    },
  }),
];

export const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange }) => {
  const [isSourceMode, setIsSourceMode] = useState(false);

  const editor = useEditor({
    extensions,
    content: value,
    onUpdate: ({ editor }) => {
      // Only sync from editor to parent if we are NOT in source mode.
      // If we are in source mode, the textarea controls the value.
      if (!isSourceMode) {
        onChange(editor.getHTML());
      }
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none p-4 min-h-[250px]',
      },
    },
  });

  // Update editor content when external value changes (e.g. initial load or typing in source mode)
  useEffect(() => {
    if (editor && !editor.isDestroyed && !isSourceMode) {
      // Compare content to avoid cursor jumping or unnecessary updates
      if (editor.getHTML() !== value) {
        editor.commands.setContent(value, { emitUpdate: false });
      }
    }
  }, [value, editor, isSourceMode]);

  const handleSourceChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  };

  return (
    <div className="bg-white border border-gray-300 rounded-md">
      <MenuBar 
        editor={editor} 
        isSourceMode={isSourceMode} 
        toggleSourceMode={() => setIsSourceMode(!isSourceMode)} 
      />
      {isSourceMode ? (
        <textarea
          value={value}
          onChange={handleSourceChange}
          className="w-full h-[400px] p-4 font-mono text-sm text-gray-800 bg-gray-50 focus:outline-none focus:bg-white resize-y"
          spellCheck={false}
        />
      ) : (
        <EditorContent editor={editor} />
      )}
    </div>
  );
};